import { useState } from 'react';
import { closeCircleOutline, documentText, sendOutline } from 'ionicons/icons';
import { useSendMessage } from '@/hooks/useContact';
import { useToast } from '@/components/ToastProvider';
import { Card, CardContent } from '@/components/Card';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { PageHeader } from '@/components/PageHeader';
import { ApiError } from '@/lib/errors';

const MAX_LENGTH = 1000;

/** Port of contact-us.page.ts / contact-us.page.html. */
export default function ContactUsPage() {
  const [message, setMessage] = useState('');
  const { showToast } = useToast();
  const sendMessage = useSendMessage();

  const trimmed = message.trim();
  const canSubmit = trimmed.length > 0 && !sendMessage.isPending;

  const handleSubmit = () => {
    sendMessage.mutate(trimmed, {
      onSuccess: () => {
        showToast('Message sent successfully');
        setMessage('');
      },
      onError: (error) =>
        showToast(
          `Failed to send message: ${
            error instanceof ApiError ? error.userMessage : 'Please try again.'
          }`,
          'danger',
        ),
    });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <PageHeader
        icon={documentText}
        title="Contact us"
        subtitle="Problems, suggestions… We’d love to hear from you."
      />

      <Card className="mt-6">
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="contact-message" className="sr-only">
              Your message
            </label>
            <textarea
              id="contact-message"
              rows={8}
              maxLength={MAX_LENGTH}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Type your message here..."
              className="w-full resize-y rounded-md border border-line-strong px-3 py-2 text-content focus-visible:border-focus focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
            />
            <p className="mt-1 text-right text-sm text-muted">
              {message.length} / {MAX_LENGTH}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              disabled={trimmed.length === 0 || sendMessage.isPending}
              onClick={() => setMessage('')}
            >
              <Icon icon={closeCircleOutline} />
              Clear
            </Button>
            <Button disabled={!canSubmit} onClick={handleSubmit}>
              <Icon icon={sendOutline} />
              {sendMessage.isPending ? 'Sending...' : 'Send message'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
